import axios from 'axios';
import { getRealtimeClient, REALTIME_CHANNELS } from '../../utils/realtimeClient';
import { getEnrollmentByProgram, getEnrollmentForAllPrograms } from './enrollmentApi';

const SYNC_TIMEOUT_MS = Number(import.meta.env?.VITE_SYNC_TIMEOUT_MS) || 10 * 60 * 1000;

export const pollSyncStatus = async (programName) => {
    const createRealtimeWatcher = async () => {
        const rt = await getRealtimeClient();
        if (!rt?.enabled || !rt?.pusher) return null;

        let settled = false;
        let timeoutId;
        let resolvePromise;
        let rejectPromise;
        const channel = rt.pusher.subscribe(REALTIME_CHANNELS.adminRefresh);

        const cleanup = () => {
            if (settled) return;
            settled = true;
            if (timeoutId) clearTimeout(timeoutId);
            channel.unbind('refresh.status', onRefreshStatus);
            channel.unbind('refresh.program.status', onProgramStatus);
        };

        const onRefreshStatus = (payload) => {
            if (programName) return;
            const status = payload?.status;
            if (status === 'complete') {
                cleanup();
                resolvePromise?.(payload);
            } else if (status === 'error') {
                cleanup();
                rejectPromise?.(new Error('Cache sync failed on the server.'));
            } else if (status === 'aborted') {
                cleanup();
                rejectPromise?.(new Error(payload?.run?.abortReason || 'Cache sync was aborted.'));
            }
        };

        const onProgramStatus = (payload) => {
            if (!programName) return;
            if (payload?.programName !== programName) return;
            const status = payload?.status;
            if (status === 'complete') {
                cleanup();
                resolvePromise?.(payload);
            } else if (status === 'error') {
                cleanup();
                rejectPromise?.(new Error(`Cache sync failed for ${programName}.`));
            } else if (status === 'aborted') {
                cleanup();
                rejectPromise?.(new Error('Cache sync was aborted.'));
            }
        };

        const promise = new Promise((resolve, reject) => {
            resolvePromise = resolve;
            rejectPromise = reject;
            channel.bind('refresh.status', onRefreshStatus);
            channel.bind('refresh.program.status', onProgramStatus);

            timeoutId = setTimeout(() => {
                cleanup();
                reject(new Error('Sync process timed out.'));
            }, SYNC_TIMEOUT_MS);
        });

        return {
            promise,
            stop: cleanup,
        };
    };

    const createPollingWatcher = ({ startDelayMs = 0 } = {}) => {
        const params = programName ? { programName } : undefined;
        let settled = false;
        let intervalId;
        let timeoutId;
        let startTimer;

        const cleanup = () => {
            if (settled) return;
            settled = true;
            if (startTimer) clearTimeout(startTimer);
            if (intervalId) clearInterval(intervalId);
            if (timeoutId) clearTimeout(timeoutId);
        };

        const promise = new Promise((resolve, reject) => {
            const finish = (fn, value) => {
                if (settled) return;
                settled = true;
                if (startTimer) clearTimeout(startTimer);
                if (intervalId) clearInterval(intervalId);
                if (timeoutId) clearTimeout(timeoutId);
                fn(value);
            };

            const checkStatus = async () => {
                try {
                    const { data } = await axios.get('/api/cache/refresh-status', { params });
                    if (data.status === 'complete') {
                        finish(resolve, data);
                    } else if (data.status === 'error') {
                        const scope = programName
                            ? `Cache sync failed for ${programName}.`
                            : 'Cache sync failed on the server.';
                        finish(reject, new Error(scope));
                    } else if (data.status === 'aborted') {
                        finish(reject, new Error(data.run?.abortReason || 'Cache sync was aborted.'));
                    }
                } catch (error) {
                    finish(reject, error);
                }
            };

            const start = () => {
                if (settled) return;
                intervalId = setInterval(checkStatus, 2000);
                checkStatus();
            };

            if (startDelayMs > 0) {
                startTimer = setTimeout(start, startDelayMs);
            } else {
                start();
            }

            timeoutId = setTimeout(() => {
                finish(reject, new Error('Sync process timed out.'));
            }, SYNC_TIMEOUT_MS);
        });

        return { promise, stop: cleanup };
    };

    const realtimeWatcher = await createRealtimeWatcher();
    const pollingWatcher = createPollingWatcher({ startDelayMs: realtimeWatcher ? 2000 : 0 });

    if (!realtimeWatcher) {
        return pollingWatcher.promise;
    }

    return new Promise((resolve, reject) => {
        let settled = false;
        const finalize = (fn, value) => {
            if (settled) return;
            settled = true;
            realtimeWatcher.stop();
            pollingWatcher.stop();
            fn(value);
        };

        realtimeWatcher.promise.then((value) => finalize(resolve, value)).catch((error) => finalize(reject, error));
        pollingWatcher.promise.then((value) => finalize(resolve, value)).catch((error) => finalize(reject, error));
    });
};

export const getCacheStatus = async () => {
    try {
        const { data } = await axios.get('/api/cache/status', {
            headers: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
                Expires: '0',
            },
        });
        return data;
    } catch (error) {
        if (error?.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

export const forceSync = async () => {
    await axios.post('/api/cache/refresh');
    await pollSyncStatus();
    return getEnrollmentForAllPrograms();
};

export const forceProgramSync = async (input) => {
    const programName = typeof input === 'string' ? input : input?.programName;
    if (!programName) {
        throw new Error('Program name is required.');
    }

    const kickoff = await axios.post(`/api/cache/refresh/program/${encodeURIComponent(programName)}`);
    const kickoffData = kickoff?.data || {};
    const isQueued = Boolean(kickoffData?.queued) || /queued refresh/i.test(String(kickoffData?.message || ''));

    if (isQueued) {
        return {
            programName,
            queued: true,
            message: kickoffData?.message || `Queued refresh for ${programName}.`,
            data: null,
            run: kickoffData?.run || null,
        };
    }

    await pollSyncStatus(programName);
    const data = await getEnrollmentByProgram(programName);
    return { programName, queued: false, data };
};

export const abortSync = async () => {
    const { data } = await axios.post('/api/cache/refresh/abort');
    return data;
};
